import type { AssistantAction, AssistantActionKind, AssistantDraft } from '../../types/assistant';

/** What an action handler can do in the app around the assistant. */
export interface AssistantActionContext {
  navigate: (path: string) => void;
  /** Opens a client-made draft in the panel (a message to a player, say), as if the assistant drafted it. */
  openDraft: (draft: AssistantDraft) => void;
  /** Sends a follow-up message for the coach, as if they typed it. */
  say: (message: string) => void;
  /** Closes the assistant panel (before leaving for another page, say). */
  close: () => void;
  /** Tells the coach something in the panel's live region and notice line. */
  notify: (message: string) => void;
}

/**
 * What an action kind does. `item` is the list item the action came from, when there is one (a
 * player row), so a handler can read the real row without another request.
 */
export type AssistantActionHandler = (
  action: AssistantAction,
  ctx: AssistantActionContext,
  item?: unknown,
) => void | Promise<void>;

const handlers = new Map<string, AssistantActionHandler>();

/** Registers what `kind` does. A later registration replaces an earlier one (a domain's own over the core fallback). */
export function registerAssistantAction(kind: AssistantActionKind, handler: AssistantActionHandler): void {
  handlers.set(kind, handler);
}

export function getAssistantAction(kind: AssistantActionKind): AssistantActionHandler | undefined {
  return handlers.get(kind);
}

/** Test only: forget every registration. */
export function resetAssistantActionsForTest(): void {
  handlers.clear();
}
